import { Axios } from 'axios';
import { injectable } from 'inversify';

import { Failure, OperationResult, Success } from '@vp/common/core/OperationResult';
import { ErrorExtractor } from '@vp/common/data/ErrorExtractor';
import { ProfileRepository } from '@vp/profile/core/interface/ProfileRepository';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ProfileResponseDto } from '@vp/profile/data/dto/ProfileResponseDto';
import { ProfileDataMapper } from '@vp/profile/data/ProfileDataMapper';

@injectable()
export class RestProfileRepository implements ProfileRepository {
  constructor(
    private readonly http: Axios,
    private readonly profileDataMapper: ProfileDataMapper,
    private readonly errorExtractor: ErrorExtractor,
  ) {}

  async unlockProfile(id: string, code: string, controller?: AbortController): Promise<OperationResult<ProfileModel>> {
    try {
      const { data } = await this.http.get<ProfileResponseDto>(`/api/profiles/${id}/full_access?pin=${code}`, {
        signal: controller?.signal,
      });

      return Success.from(this.profileDataMapper.toProfile(data));
    } catch (error) {
      const message = this.errorExtractor.extract(error);
      return Failure.from(message);
    }
  }

  async loadProfile(id: string, controller?: AbortController): Promise<OperationResult<ProfileModel>> {
    try {
      const { data } = await this.http.get<ProfileResponseDto>(`/api/profiles/${id}`, { signal: controller?.signal });
      return Success.from(this.profileDataMapper.toProfile(data));
    } catch (error) {
      const message = this.errorExtractor.extract(error);
      return Failure.from(message);
    }
  }

  async activateProfile(id: string, controller?: AbortController): Promise<OperationResult<ProfileModel>> {
    try {
      const { data } = await this.http.put<ProfileResponseDto>(`/api/profiles/${id}/activate`, { signal: controller?.signal });
      return Success.from(this.profileDataMapper.toProfile(data));
    } catch (error) {
      const message = this.errorExtractor.extract(error);
      return Failure.from(message);
    }
  }
}
